import { KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { RiArrowRightLine, RiCloseLine, RiDeleteBin6Line } from "react-icons/ri";
import { DirectionCode, PhotoSection } from "./photoModel";
import { BarcodeNode, FileNode, MatcodeNode, SectionNode, checkStateOf } from "./treeModel";
import TreeFolderRow from "./TreeFolderRow";
import TreeFileRow from "./TreeFileRow";
import { RowMenuItem } from "./TreeRowMenu";

type Seat = { posinset: number; setsize: number };
type Row = Seat &
  (
    | { id: string; level: 1; type: "barcode"; node: BarcodeNode; parentId: null }
    | { id: string; level: 2; type: "matcode"; node: MatcodeNode; parentId: string }
    | { id: string; level: 3; type: "section"; node: SectionNode; parentId: string }
    | { id: string; level: 4; type: "file"; node: FileNode; parentId: string }
  );

export interface PhotoTreeProps {
  tree: BarcodeNode[];
  /** Derived upload names, keyed by draft id. */
  fileNames: Record<string, string>;
  selected: Set<string>;
  expanded: Set<string>;
  disabled?: boolean;
  onToggleExpand(id: string): void;
  onSetExpanded(id: string, open: boolean): void;
  onSetSelection(ids: string[], on: boolean): void;
  onToggleSelect(id: string): void;
  onOpen(id: string): void;
  onSetDirection(id: string, d: DirectionCode): void;
  onMoveSection(ids: string[], s: PhotoSection): void;
  onSetMain(id: string): void;
  onDeleteMany(ids: string[]): void;
  onDelete?(id: string): void;
  onRetry?(id: string): void;
  onDismissSection?(id: string): void;
}

/** Escapes a value for use inside a quoted attribute selector. */
const attr = (value: string) => value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

export default function PhotoTree({
  tree,
  fileNames,
  selected,
  expanded,
  disabled,
  onToggleExpand,
  onSetExpanded,
  onSetSelection,
  onToggleSelect,
  onOpen,
  onSetDirection,
  onMoveSection,
  onSetMain,
  onDeleteMany,
  onDelete,
  onRetry,
  onDismissSection,
}: PhotoTreeProps) {
  const [focusId, setFocusId] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  // The visible rows, in the order the folder tree reads.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    tree.forEach((barcode, b) => {
      out.push({ id: barcode.id, level: 1, type: "barcode", node: barcode, parentId: null,
        posinset: b + 1, setsize: tree.length });
      if (!expanded.has(barcode.id)) return;
      barcode.matcodes.forEach((matcode, m) => {
        out.push({ id: matcode.id, level: 2, type: "matcode", node: matcode, parentId: barcode.id,
          posinset: m + 1, setsize: barcode.matcodes.length });
        if (!expanded.has(matcode.id)) return;
        matcode.sections.forEach((section, x) => {
          out.push({ id: section.id, level: 3, type: "section", node: section, parentId: matcode.id,
            posinset: x + 1, setsize: matcode.sections.length });
          if (!expanded.has(section.id)) return;
          section.files.forEach((file, f) => {
            out.push({ id: file.id, level: 4, type: "file", node: file, parentId: section.id,
              posinset: f + 1, setsize: section.files.length });
          });
        });
      });
    });
    return out;
  }, [tree, expanded]);

  const activeId = focusId && rows.some((r) => r.id === focusId) ? focusId : rows[0]?.id ?? null;

  const focusRow = useCallback((id: string) => {
    setFocusId(id);
    requestAnimationFrame(() => {
      container.current?.querySelector<HTMLElement>(`[data-row-id="${attr(id)}"]`)?.focus();
    });
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Only the row itself navigates. Without this, Space and Enter pressed on a checkbox,
    // an angle picker or a menu item bubble up here and get preventDefault()-ed, which
    // makes every control inside a row unusable from the keyboard.
    if (!(e.target as HTMLElement).hasAttribute("data-row-id")) return;
    if (!activeId) return;
    const index = rows.findIndex((r) => r.id === activeId);
    if (index === -1) return;
    const row = rows[index];
    const isFolder = row.type !== "file";
    const open = expanded.has(row.id);
    const move = (to: number) => {
      const next = rows[to];
      if (next) focusRow(next.id);
    };

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        move(index + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        move(index - 1);
        break;
      case "ArrowRight":
        e.preventDefault();
        if (isFolder && !open) onSetExpanded(row.id, true);
        else move(index + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (isFolder && open) onSetExpanded(row.id, false);
        else if (row.parentId) focusRow(row.parentId);
        break;
      case "Home":
        e.preventDefault();
        move(0);
        break;
      case "End":
        e.preventDefault();
        move(rows.length - 1);
        break;
      case " ":
        e.preventDefault();
        if (row.type === "file") onToggleSelect(row.id);
        else {
          const ids = row.node.fileIds;
          onSetSelection(ids, checkStateOf(ids, selected) !== "checked");
        }
        break;
      case "Enter":
        e.preventDefault();
        if (row.type === "file") onOpen(row.id);
        else onToggleExpand(row.id);
        break;
      default:
        break;
    }
  };

  const folderMenu = (row: Row): RowMenuItem[] => {
    if (row.type === "file") return [];
    const ids = row.node.fileIds;
    const items: RowMenuItem[] = [];

    if (row.type === "section") {
      const other: PhotoSection = row.node.section === "cover" ? "packshot" : "cover";
      items.push({
        label: `ย้ายทุกรูปไป ${other}/`,
        icon: <RiArrowRightLine size={14} />,
        onSelect: () => onMoveSection(ids, other),
        disabled: disabled || ids.length === 0,
      });
      if (ids.length === 0 && onDismissSection) {
        items.push({
          label: "เอาโฟลเดอร์ว่างออก",
          icon: <RiCloseLine size={14} />,
          onSelect: () => onDismissSection(row.id),
          disabled,
        });
      }
    }

    const scope =
      row.type === "barcode" ? "barcode นี้" : row.type === "matcode" ? "matcode นี้" : "โฟลเดอร์นี้";
    items.push({
      label: `ลบทั้ง${scope}`,
      icon: <RiDeleteBin6Line size={14} />,
      onSelect: () => onDeleteMany(ids),
      danger: true,
      disabled: disabled || ids.length === 0,
    });
    return items;
  };

  if (rows.length === 0) return null;

  return (
    <div
      ref={container}
      role="tree"
      aria-label="โครงสร้างโฟลเดอร์ที่นำเข้า"
      aria-multiselectable="true"
      onKeyDown={onKeyDown}
      className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white"
    >
      {rows.map((row) => {
        if (row.type === "file") {
          return (
            <TreeFileRow
              key={row.id}
              node={row.node}
              derivedName={fileNames[row.id]}
              level={row.level}
              posinset={row.posinset}
              setsize={row.setsize}
              selected={selected.has(row.id)}
              focused={activeId === row.id}
              disabled={disabled}
              onToggleSelect={onToggleSelect}
              onOpen={onOpen}
              onSetDirection={onSetDirection}
              onSetSection={(id, s) => onMoveSection([id], s)}
              onSetMain={onSetMain}
              onDelete={onDelete}
              onRetry={onRetry}
              onFocusRow={() => setFocusId(row.id)}
            />
          );
        }

        const ids = row.node.fileIds;
        const subtitle =
          row.type === "barcode"
            ? `${row.node.matcodes.length} matcode`
            : row.type === "matcode"
            ? row.node.info?.status === "valid"
              ? row.node.info.name || undefined
              : undefined
            : undefined;

        const path =
          row.type === "barcode"
            ? row.node.barcode
            : row.type === "matcode"
            ? `${row.node.barcode}/${row.node.matCode}`
            : `${row.node.barcode}/${row.node.matCode}/${row.node.section}`;

        const hasChildren =
          row.type === "barcode"
            ? row.node.matcodes.length > 0
            : row.type === "matcode"
            ? row.node.sections.length > 0
            : row.node.files.length > 0;

        return (
          <TreeFolderRow
            key={row.id}
            kind={row.type}
            rowId={row.id}
            name={row.type === "section" ? row.node.section : row.type === "barcode" ? row.node.barcode : row.node.matCode}
            path={path}
            subtitle={subtitle}
            level={row.level}
            posinset={row.posinset}
            setsize={row.setsize}
            stats={row.node.stats}
            expanded={expanded.has(row.id)}
            hasChildren={hasChildren}
            checkState={checkStateOf(ids, selected)}
            focused={activeId === row.id}
            menuItems={folderMenu(row)}
            onToggleExpand={() => onToggleExpand(row.id)}
            onToggleCheck={() => onSetSelection(ids, checkStateOf(ids, selected) !== "checked")}
            onFocusRow={() => setFocusId(row.id)}
          />
        );
      })}
    </div>
  );
}
